import { PayloadAction, createSlice, createSelector } from '@reduxjs/toolkit';
import { initialMessage, userMessage } from 'constants/ui.constants';
import { IPaymentCategory } from 'constants/payment-categories.constants';
import { flattenPaymentCategories } from 'services/payment-categories.service';
import type { PaymentCategoryOption } from 'components/PaymentCategoryPicker/types';

interface IPaymentCategoriesState {
	categories: IPaymentCategory[];
	currentCategory: IPaymentCategory | null;
	isLoading: boolean;
	error: userMessage;
	translationsMap: Record<string, { en?: string; he?: string }>;
}

const initialState: IPaymentCategoriesState = {
	categories: [],
	currentCategory: null,
	isLoading: false,
	error: initialMessage,
	translationsMap: {},
};

export const paymentCategoriesSlice = createSlice({
	name: 'paymentCategories',
	initialState,
	reducers: {
		setCategories: (state, { payload }: PayloadAction<IPaymentCategory[]>) => {
			if (state.categories === payload) return;
			state.categories = [...payload];
		},
		setCurrentCategory: (state, { payload }: PayloadAction<IPaymentCategory | null>) => {
			if (state.currentCategory === payload) return;
			state.currentCategory = { ...payload } as IPaymentCategory;
		},
		setLoading: (state, { payload }: PayloadAction<boolean>) => {
			state.isLoading = payload;
		},
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		clearError: (state) => {
			state.error = initialMessage;
		},
		setTranslationsMap: (state, { payload }: PayloadAction<Record<string, { en?: string; he?: string }>>) => {
			if (state.translationsMap === payload) return;
			state.translationsMap = { ...payload };
		},
	},
});

// Selectors
export const getPaymentCategories = (state: any): IPaymentCategory[] => {
	return state.paymentCategories.categories;
};

export const getCurrentCategory = (state: any): IPaymentCategory | null => {
	return state.paymentCategories.currentCategory;
};

export const getPaymentCategoriesLoading = (state: any): boolean => {
	return state.paymentCategories.isLoading;
};

export const getPaymentCategoriesError = (state: any): userMessage => {
	return state.paymentCategories.error;
};

export const getPaymentCategoriesTranslationsMap = (state: any): Record<string, { en?: string; he?: string }> => {
	return state.paymentCategories.translationsMap;
};

/**
 * Selector that returns a translation function for payment category names
 * Similar to selectTranslations for CSV translations
 * Usage: const categoryTranslation = useAppSelector(selectPaymentCategoryTranslation);
 *        categoryTranslation('goods') → 'טובין'
 */
export const selectPaymentCategoryTranslation = createSelector(
	[getPaymentCategoriesTranslationsMap],
	(translationsMap) =>
		(categoryName: string, fallback?: string): string => {
			const key = `payment_category.name.${categoryName}`;
			return translationsMap?.[key]?.he || fallback || categoryName;
		}
);

/**
 * Memoized selector that flattens the category tree into flat options for PaymentCategoryPicker
 * Only returns active leaf categories (for pickers/selection)
 */
export const selectFlattenedCategoryOptions = createSelector(
	[getPaymentCategories],
	(categories): PaymentCategoryOption[] => flattenPaymentCategories(categories)
);

/**
 * Memoized selector that flattens ALL categories including inactive ones.
 * Use this for display views (PaymentCategoriesView) where we need to show
 * already-linked categories even if they are inactive.
 */
export const selectAllFlattenedCategoryOptions = createSelector(
	[getPaymentCategories],
	(categories): PaymentCategoryOption[] => flattenPaymentCategories(categories, true)
);

export const { setCategories, setCurrentCategory, setLoading, setError, clearError, setTranslationsMap } =
	paymentCategoriesSlice.actions;

export default paymentCategoriesSlice.reducer;
